import { Context } from '../context/Context';
import { Texture } from '../texture/Texture';

/**
 * BMFont-style glyph metric. Pixel coordinates are atlas pixels at the
 * authored font size (see {@link FontInfo.size}).
 */
export interface GlyphData {
  /** Codepoint. */
  id: number;
  /** UTF-8 char (for debugging). */
  char: string;
  /** Glyph rect width in atlas pixels. */
  width: number;
  /** Glyph rect height in atlas pixels. */
  height: number;
  /** Pen x-offset (atlas px) when drawing. */
  xoffset: number;
  /** Pen y-offset (atlas px, from top of line). */
  yoffset: number;
  /** Pen advance (atlas px) after drawing. */
  xadvance: number;
  /** Glyph top-left x in atlas. */
  x: number;
  /** Glyph top-left y in atlas. */
  y: number;
  /** Atlas page index (we only support single-page atlases). */
  page: number;
  /** Channel mask (BMFont legacy). 15 = packed in RGBA. Unused at runtime. */
  chnl?: number;
  /** Charset order index. BMFont legacy, unused at runtime. */
  index?: number;
}

export interface FontInfo {
  face: string;
  /** Authored font size — the pixel size at which all metrics are expressed. */
  size: number;
  bold?: number;
  italic?: number;
  /** Extra BMFont legacy fields the runtime ignores. */
  charset?: string[];
  unicode?: number;
  stretchH?: number;
  smooth?: number;
  aa?: number;
  padding?: number[];
  spacing?: number[];
  outline?: number;
}

export interface FontCommon {
  /** Line height in atlas pixels. */
  lineHeight: number;
  /** Baseline distance from top of line in atlas pixels. */
  base: number;
  /** Atlas width in pixels. */
  scaleW: number;
  /** Atlas height in pixels. */
  scaleH: number;
  /** Extra BMFont legacy fields the runtime ignores. */
  pages?: number;
  packed?: number;
  alphaChnl?: number;
  redChnl?: number;
  greenChnl?: number;
  blueChnl?: number;
}

export interface FontDistanceField {
  fieldType: 'msdf' | 'sdf';
  /** SDF range expressed in atlas pixels (msdfgen --pxrange). */
  distanceRange: number;
}

export interface FontKerning {
  first: number;
  second: number;
  amount: number;
}

/**
 * BMFont-style font data, as emitted by `cli/src/build-fonts.ts`. Page is a
 * single inline data URI (we only support single-page atlases).
 */
export interface FontData {
  pages: string[];
  chars: GlyphData[];
  info: FontInfo;
  common: FontCommon;
  distanceField: FontDistanceField;
  kernings: FontKerning[];
}

function decodeImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to decode MSDF atlas image`));
    img.src = source;
  });
}

/**
 * Multi-channel signed distance field font. Owns one GL texture (the atlas)
 * plus glyph metrics and kerning. Construct via {@link MsdfFont.load}.
 */
export class MsdfFont {
  public readonly texture: Texture;
  public readonly atlasWidth: number;
  public readonly atlasHeight: number;
  public readonly lineHeight: number;
  public readonly base: number;
  public readonly fontSize: number;
  public readonly distanceRange: number;
  public readonly fieldType: 'msdf' | 'sdf';

  private readonly glyphById = new Map<number, GlyphData>();
  /** Kerning lookup: `(first << 16) | second` → amount. */
  private readonly kerningByKey = new Map<number, number>();

  private constructor(data: FontData, texture: Texture) {
    this.texture = texture;
    this.atlasWidth = data.common.scaleW;
    this.atlasHeight = data.common.scaleH;
    this.lineHeight = data.common.lineHeight;
    this.base = data.common.base;
    this.fontSize = data.info.size;
    this.distanceRange = data.distanceField.distanceRange;
    this.fieldType = data.distanceField.fieldType;

    for (const c of data.chars) this.glyphById.set(c.id, c);
    for (const k of data.kernings) {
      this.kerningByKey.set((k.first << 16) | k.second, k.amount);
    }
  }

  /** Decodes the atlas image and uploads to a GL texture. */
  public static async load(ctx: Context, data: FontData): Promise<MsdfFont> {
    if (data.pages.length !== 1) {
      throw new Error(`MsdfFont: only single-page atlases supported (got ${data.pages.length}).`);
    }
    const img = await decodeImage(data.pages[0]);
    const gl = ctx.gl;
    const glTexture = gl.createTexture();
    if (!glTexture) throw new Error('Failed to create MSDF texture');

    gl.bindTexture(gl.TEXTURE_2D, glTexture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    // MSDF sampling MUST stay linear with NO mipmaps — the distance field
    // breaks if downsampled, so the visual sharpness is preserved by the
    // screen-space derivatives in the fragment shader instead.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);

    const texture = Texture.fromWebGlTexture(glTexture);
    return new MsdfFont(data, texture);
  }

  public getGlyph(codepoint: number): GlyphData | undefined {
    return this.glyphById.get(codepoint);
  }

  public getKerning(first: number, second: number): number {
    return this.kerningByKey.get((first << 16) | second) ?? 0;
  }

  public destroy(gl: WebGL2RenderingContext): void {
    if (this.texture.glTexture) gl.deleteTexture(this.texture.glTexture);
  }
}
